var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { USER_INTENT, otherIssuer, } from "../lib/constants";
import { bluredIssuerSearchbox, clickedCancelSearch, clickedIssuerNotFound, clickedIssuerOption, focusedIssuerSearchbox, } from "../lib/Avo";
import { CloseButtonSVG } from "./Icons/Icons";
import Fuse from "fuse.js";
import Image from "./Image";
import IssuerInfo from "./IssuerInfo/IssuerInfo";
import RouteTitle from "./RouteTitle";
import classNames from "classnames";
import debounce from "lodash/debounce";
import matchIssuers from "../utils/matchIssuers";
import useAppSelector from "../hooks/useAppSelector";
import useApplication from "../features/configuration/useApplication";
import useCloseClickHandler from "../hooks/useCloseClickHandler";
import useEventPublisher from "../hooks/useEventPublisher";
import useIsSupportedIssuer from "../hooks/useIsSupportedIssuer";
var defaultState = {
    value: "",
    searchFocused: false,
};
function selectIssuerReducer(prevState, nextState) {
    return __assign(__assign({}, prevState), nextState);
}
function IssuerTrackItem(_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    return (React.createElement("div", __assign({ className: "group relative h-[72px] border-t border-[#f2f2f2] px-4 hover:bg-black/5 md:px-6" }, rest), children));
}
function FooterButton(_a) {
    var className = _a.className, rest = __rest(_a, ["className"]);
    return (React.createElement("button", __assign({ className: classNames("flex h-14 w-full items-center justify-center border-none bg-transparent", className) }, rest)));
}
export default function SelectIssuerList(_a) {
    var _b, _c, _d;
    var title = _a.title, insuranceType = _a.insuranceType, issuers = _a.issuers, initialIssuers = _a.initialIssuers, handleSelectIssuer = _a.handleSelectIssuer, disableSearchBox = _a.disableSearchBox, issuerListLastNode = _a.issuerListLastNode, onCloseClick = _a.onCloseClick, onFooterClick = _a.onFooterClick;
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics; }).commonAnalyticsProperties;
    var _e = useAppSelector(function (state) { return state.configuration; }), hideUninsuredButton = _e.hideUninsuredButton, closeOnUnsupportedIssuer = _e.closeOnUnsupportedIssuer, isSavvyWidget = _e.isSavvyWidget;
    var inputRef = React.useRef(null);
    var application = useApplication();
    var _f = React.useReducer(selectIssuerReducer, defaultState), _g = _f[0], value = _g.value, searchFocused = _g.searchFocused, setState = _f[1];
    var eventPublisher = useEventPublisher();
    var closeClickHandler = useCloseClickHandler();
    var isSupportedIssuer = useIsSupportedIssuer();
    var fuse = new Fuse(
    // we return nkbc or "Nationally Known Brand Carrier" as a mask for Progressive when showing ads / quotes
    // we NEVER want to show nkbc as an option for users to select
    issuers.filter(function (_a) {
        var id = _a.id;
        return id !== "nkbc";
    }), {
        keys: ["name"],
        threshold: 0.4,
    });
    var isSearching = Boolean(value);
    var visibleIssuers = isSearching
        ? fuse.search(value)
        : matchIssuers(initialIssuers, issuers);
    var exitButtonText = "Exit";
    var debouncedSearchEventPublish = React.useMemo(function () {
        return debounce(function (searchTerm) {
            if (searchTerm.length > 0) {
                eventPublisher.searchIssuer(searchTerm);
            }
        }, 600);
    }, [eventPublisher]);
    function handleChange(_a) {
        var target = _a.target;
        setState({ value: target.value });
        debouncedSearchEventPublish(target.value);
    }
    function handleIssuerClick(issuer) {
        return function () {
            var linkingSupported = isSupportedIssuer(issuer);
            clickedIssuerOption(__assign(__assign({}, commonAnalyticsProperties), { issuer: issuer.id }));
            eventPublisher.selectIssuer(issuer.id, { linkingSupported: linkingSupported });
            handleSelectIssuer(issuer);
            if (!linkingSupported) {
                if (closeOnUnsupportedIssuer) {
                    return closeClickHandler({
                        userIntent: USER_INTENT.PROCEED_MANUALLY,
                    });
                }
            }
        };
    }
    function handleCloseClick() {
        clickedIssuerNotFound(__assign(__assign({}, commonAnalyticsProperties), { searchboxValue: value }));
        onCloseClick === null || onCloseClick === void 0 ? void 0 : onCloseClick(otherIssuer);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "border-b-[.5px] border-gray-200 px-4 py-4 md:px-6 desktop:pt-2" },
            React.createElement(RouteTitle, { variant: "small", className: classNames("font-bold leading-none") }, title),
            !disableSearchBox ? (React.createElement("form", { className: "relative mt-2 rounded border border-[#949494]", onSubmit: function (event) {
                    event.preventDefault();
                } },
                React.createElement("div", { className: "pointer-events-none absolute left-0 top-0 inline-flex h-10 w-10 flex-none items-center justify-center" },
                    React.createElement(Image, { src: "/assets/icons/search.svg", alt: "" })),
                React.createElement("input", { className: "h-10 w-full rounded-md py-0 pl-10 pr-3 text-base", "data-testid": "select-issuer-search", type: "text", name: "search", value: value, ref: inputRef, onChange: handleChange, onFocus: function () {
                        setState({ searchFocused: true });
                        focusedIssuerSearchbox(commonAnalyticsProperties);
                    }, onBlur: function () {
                        setState({ searchFocused: false });
                        bluredIssuerSearchbox(__assign(__assign({}, commonAnalyticsProperties), { searchboxValue: value }));
                    }, placeholder: "Search for your carrier", autoComplete: "off" }),
                searchFocused || isSearching ? (React.createElement("button", { className: "absolute right-0 top-0 inline-flex h-full w-10 items-center justify-center border-none bg-transparent p-0 outline-none", onClick: function () {
                        clickedCancelSearch(commonAnalyticsProperties);
                        setState({ value: "", searchFocused: false });
                    } },
                    React.createElement(CloseButtonSVG, { fill: "#111", size: 16 }))) : null)) : null),
        React.createElement("div", { className: "w-full grow overflow-y-auto" },
            React.createElement("div", { className: classNames("flex min-h-full flex-col pb-6"), id: "issuer-track" },
                visibleIssuers.length > 0 ? (React.createElement(React.Fragment, null,
                    visibleIssuers.map(function (_a) {
                        var item = _a.item;
                        return (React.createElement(IssuerTrackItem, { key: item.id },
                            React.createElement(IssuerInfo, { id: item.id, name: item.name, site: item.site, displayLogos: true, onClick: handleIssuerClick(item), style: { padding: "12px 0" }, insuranceType: insuranceType })));
                    }),
                    value ? (React.createElement("button", { className: "py-4 font-bold leading-none text-black-100 underline", onClick: handleCloseClick }, "My insurer is not listed")) : null)) : value ? (React.createElement("div", { className: "m-auto flex w-72 flex-col items-center text-black-300" },
                    React.createElement("div", { className: "mb-5 flex leading-none" },
                        React.createElement(Image, { src: "/assets/icons/magnifying-glass.svg", alt: "" })),
                    React.createElement("p", { className: "mb-5 text-center text-2xl" }, "Insurer not found"),
                    React.createElement("button", { className: "font-bold leading-none text-black-100 underline", onClick: handleCloseClick }, isSavvyWidget ? "My insurer is not listed" : exitButtonText))) : (React.createElement("div", { className: "m-auto flex w-72 flex-col items-center text-black-300" },
                    React.createElement("div", { className: "mb-4 flex leading-none" },
                        React.createElement(Image, { src: "/assets/icons/plaid-home.svg", alt: "" })),
                    React.createElement("p", { className: "mb-2 text-center leading-5" }, "Search through dozens of insurance companies"))),
                issuerListLastNode && !isSearching
                    ? issuerListLastNode({ inputRef: inputRef })
                    : null)),
        !hideUninsuredButton ? (React.createElement("div", { className: "mt-auto bg-white text-center", style: {
                boxShadow: "0px -4px 4px rgba(0, 0, 0, 0.15)",
            } }, ((_b = application.not_insured_link) === null || _b === void 0 ? void 0 : _b.visible) ? (React.createElement(FooterButton, { className: "text-base font-bold text-trellis-black", "data-cy": "not-insured", onClick: onFooterClick, id: "tile-notinsured" }, (_d = (_c = application.not_insured_link) === null || _c === void 0 ? void 0 : _c.text) === null || _d === void 0 ? void 0 : _d[insuranceType])) : null)) : null));
}
